import type {PropertyDefinitionBase} from '@github-ui/custom-properties-types'
import {Fragment, type ReactElement, type ReactNode, type Ref, type RefCallback, useImperativeHandle} from 'react'

import type {PropertyField} from '../../hooks/use-edit-custom-properties'
import {useRefs} from '../../hooks/use-refs'
import {EditValueRow, FormReadValueRow, PropertyValueRowContainer, ReadValueRow} from './PropertiesValuesTableRows'

export interface PropertiesValuesTableRef {
  focusInput: (propertyName: string) => void
}
export interface RowProps<T extends PropertyDefinitionBase> {
  propertyField: PropertyField | undefined
  definition: T
}

interface PropertiesValuesTableProps<T extends PropertyDefinitionBase> {
  definitions: T[]
  propertyValuesMap: Record<string, PropertyField>
  rowRenderer: (rowProps: RowProps<T>, setRef?: RefCallback<HTMLElement>) => ReactNode
  ref?: Ref<PropertiesValuesTableRef>
}

export const PropertiesValuesTable = <T extends PropertyDefinitionBase>(props: PropertiesValuesTableProps<T>) => {
  const {definitions, propertyValuesMap, rowRenderer, ref} = props
  const {getRef, setRef} = useRefs<Record<string, unknown>>()

  useImperativeHandle(ref, () => ({
    focusInput: (propertyName: string) => {
      getRef(propertyName)?.focus()
    },
  }))

  if (definitions.length === 0) {
    return null
  }

  return (
    <div>
      {definitions.map((definition: T) => {
        const {propertyName} = definition
        const propertyField = propertyValuesMap[propertyName]

        return <Fragment key={propertyName}>{rowRenderer({definition, propertyField}, setRef(propertyName))}</Fragment>
      })}
    </div>
  )
}

PropertiesValuesTable.displayName = 'PropertiesValuesTable'

PropertiesValuesTable.EditValueRow = EditValueRow
PropertiesValuesTable.ReadValueRow = ReadValueRow
PropertiesValuesTable.FormReadValueRow = FormReadValueRow
PropertiesValuesTable.ValueRowContainer = PropertyValueRowContainer
